// Função para validar data de nascimento
export const validateDataNascimento = (data: string): boolean => {
  if (!data.trim()) return false;

  // Verifica se tem o formato DD/MM/AAAA
  const regexData = /^(\d{2})\/(\d{2})\/(\d{4})$/;
  const match = data.match(regexData);

  if (!match) return false;

  const [, dia, mes, ano] = match;
  const diaNum = parseInt(dia, 10);
  const mesNum = parseInt(mes, 10);
  const anoNum = parseInt(ano, 10);

  // Verifica se os valores estão em ranges válidos
  if (mesNum < 1 || mesNum > 12) return false;
  if (diaNum < 1 || diaNum > 31) return false;

  // Verifica se a data é válida usando Date
  const dataObj = new Date(anoNum, mesNum - 1, diaNum);
  if (
    dataObj.getDate() !== diaNum ||
    dataObj.getMonth() !== mesNum - 1 ||
    dataObj.getFullYear() !== anoNum
  ) {
    return false;
  }

  // Verifica se não é uma data futura
  const hoje = new Date();
  if (dataObj > hoje) return false;

  // Verifica se não é muito antiga (mais de 120 anos)
  const idadeMaxima = new Date();
  idadeMaxima.setFullYear(idadeMaxima.getFullYear() - 120);
  if (dataObj < idadeMaxima) return false;

  return true;
};

// Função para validar CPF
export const validateCPF = (cpf: string): boolean => {
  if (!cpf.trim()) return false;

  // Remove pontos e traços
  const cpfLimpo = cpf.replace(/[^\d]/g, "");

  // Verifica se tem 11 dígitos
  if (cpfLimpo.length !== 11) return false;

  // Verifica se não são todos os dígitos iguais
  if (/^(\d)\1{10}$/.test(cpfLimpo)) return false;

  // Validação do primeiro dígito verificador
  let soma = 0;
  for (let i = 0; i < 9; i++) {
    soma += parseInt(cpfLimpo.charAt(i)) * (10 - i);
  }
  let resto = soma % 11;
  let digito1 = resto < 2 ? 0 : 11 - resto;

  if (parseInt(cpfLimpo.charAt(9)) !== digito1) return false;

  // Validação do segundo dígito verificador
  soma = 0;
  for (let i = 0; i < 10; i++) {
    soma += parseInt(cpfLimpo.charAt(i)) * (11 - i);
  }
  resto = soma % 11;
  let digito2 = resto < 2 ? 0 : 11 - resto;

  if (parseInt(cpfLimpo.charAt(10)) !== digito2) return false;

  return true;
};

// Função para validar Email
export const validateEmail = (email: string): boolean => {
  if (!email.trim()) return false;

  // Regex para validar formato de email
  const regexEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!regexEmail.test(email)) return false;

  // Verifica se não tem espaços
  if (email.includes(" ")) return false;

  // Verifica se não começa ou termina com ponto
  if (email.startsWith(".") || email.endsWith(".")) return false;

  // Verifica se tem pelo menos um caractere antes e depois do @
  const partes = email.split("@");
  if (partes.length !== 2) return false;
  if (partes[0].length === 0 || partes[1].length === 0) return false;

  // Verifica se o domínio tem pelo menos um ponto
  if (!partes[1].includes(".")) return false;

  return true;
};

// Função para validar CEP
export const validateCEP = (cep: string): boolean => {
  if (!cep.trim()) return false;

  // Remove formatação
  const cepLimpo = cep.replace(/[^\d]/g, "");

  // Verifica se tem 8 dígitos
  if (cepLimpo.length !== 8) return false;

  return true;
};

// Função para validar número do cartão
export const validateNumeroCartao = (numero: string): boolean => {
  if (!numero.trim()) return false;

  // Remove espaços
  const numeroLimpo = numero.replace(/\s/g, "");

  // Verifica se tem entre 13 e 19 dígitos (padrão internacional)
  if (numeroLimpo.length < 13 || numeroLimpo.length > 19) return false;

  // Verifica se são apenas números
  if (!/^\d+$/.test(numeroLimpo)) return false;

  return true;
};

// Função para validar validade do cartão
export const validateValidadeCartao = (validade: string): boolean => {
  if (!validade.trim()) return false;

  // Verifica formato MM/AA
  const regexValidade = /^(\d{2})\/(\d{2})$/;
  const match = validade.match(regexValidade);

  if (!match) return false;

  const [, mes, ano] = match;
  const mesNum = parseInt(mes, 10);
  const anoNum = parseInt(ano, 10);

  // Verifica se o mês está entre 1 e 12
  if (mesNum < 1 || mesNum > 12) return false;

  // Verifica se não é uma data passada
  const agora = new Date();
  const anoAtual = agora.getFullYear() % 100; // Últimos 2 dígitos do ano
  const mesAtual = agora.getMonth() + 1;

  if (anoNum < anoAtual || (anoNum === anoAtual && mesNum < mesAtual)) {
    return false;
  }

  return true;
};

// Função para validar CVV
export const validateCVV = (cvv: string): boolean => {
  if (!cvv.trim()) return false;

  // Verifica se tem 3 ou 4 dígitos
  if (cvv.length < 3 || cvv.length > 4) return false;

  // Verifica se são apenas números
  if (!/^\d+$/.test(cvv)) return false;

  return true;
};

// Interface para campos obrigatórios
export interface CampoObrigatorio {
  campo: string;
  nome: string;
}

// Função genérica para validar campos obrigatórios
export const validateCamposObrigatorios = (
  campos: CampoObrigatorio[]
): { isValid: boolean; camposVazios: string[] } => {
  const camposVazios = campos
    .filter(({ campo }) => {
      // Garante que é string antes de chamar trim
      const valor = campo ?? "";
      return String(valor).trim() === "";
    })
    .map(({ nome }) => nome);

  return {
    isValid: camposVazios.length === 0,
    camposVazios,
  };
};
